import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import OrganizationUpgradeModal from './OrganizationUpgradeModal';

// Lock icon SVG
const LockIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="11" width="18" height="11" rx="2" stroke="#617BFF" strokeWidth="2"/>
    <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="#617BFF" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="12" cy="16" r="1.5" fill="#617BFF"/>
  </svg>
);

// Organization icon SVG
const OrgIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="9" cy="7" r="4" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface FeatureItem {
  title: string;
  description: string;
}

interface OrganizationUpgradePromptProps {
  title?: string;
  description?: string;
  featureName?: string;
  features?: FeatureItem[];
}

const OrganizationUpgradePrompt: React.FC<OrganizationUpgradePromptProps> = ({
  title = 'Organization Account Required',
  description = 'This feature is only available for organization accounts. Upgrade to unlock team collaboration, advanced security features, and more.',
  featureName = 'this feature',
  features,
}) => {
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleUpgrade = () => {
    setShowUpgradeModal(true);
  };

  const handleLearnMore = () => {
    window.open('https://docs.flashback.tech/organization', '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        px: 3,
        textAlign: 'center',
      }}
    >
      {/* Card container */}
      <Box
        sx={{
          maxWidth: 520,
          width: '100%',
          bgcolor: 'white',
          borderRadius: '16px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
          p: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
        }}
      >
        {/* Icon container */}
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            bgcolor: 'rgba(97, 123, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
          }}
        >
          <OrgIcon />
        </Box>

        {/* Lock badge */}
        <Box
          sx={{
            position: 'relative',
            mt: -5,
            ml: 8,
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'white',
            border: '2px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LockIcon />
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#181D27',
            mt: 1,
          }}
        >
          {title}
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 400,
            color: '#535862',
            lineHeight: 1.6,
            maxWidth: 400,
          }}
        >
          {description}
        </Typography>

        {/* Features list */}
        <Box sx={{ width: '100%', mt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: features ? 2 : 1.5, textAlign: 'left' }}>
            {features ? (
              // Custom features with title and description
              features.map((feature, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: 'rgba(97, 123, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mb: 0.25 }}>
                      {feature.title}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#535862', lineHeight: 1.5 }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              // Default simple features
              [
                'Team collaboration & member management',
                'Private bridge nodes for secure data transfer',
                'Advanced security & access controls',
                'Centralized billing & usage analytics',
              ].map((feature, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: 'rgba(97, 123, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Box>
                  <Typography sx={{ fontSize: '14px', color: '#535862' }}>
                    {feature}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>

        {/* CTA Button */}
        <Box sx={{ mt: 2, width: '100%' }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleUpgrade}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              bgcolor: '#617BFF',
              color: 'white',
              fontWeight: 600,
              fontSize: '14px',
              py: 1.5,
              boxShadow: '0 4px 12px rgba(97, 123, 255, 0.3)',
              '&:hover': {
                bgcolor: '#5A6FE8',
                boxShadow: '0 6px 16px rgba(97, 123, 255, 0.4)',
              },
            }}
          >
            Switch to Organization
          </Button>
        </Box>

        {/* Contact sales link */}
        <Typography
          sx={{
            fontSize: '13px',
            color: '#717680',
            mt: 1,
          }}
        >
          Need help?{' '}
          <Box
            component="a"
            href="mailto:support@flashback.tech"
            sx={{
              color: '#617BFF',
              textDecoration: 'none',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Contact our team
          </Box>
        </Typography>
      </Box>

      {/* Upgrade Modal */}
      <OrganizationUpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName={featureName}
        onSuccess={() => navigate('/organization')}
      />
    </Box>
  );
};

export default OrganizationUpgradePrompt;
